import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Languages } from 'lucide-react';
import { TranscriptLanguage, transcriptLanguages } from '../../data/transcriptLanguages';
interface TranscriptTranslateBarProps {
  isLight: boolean;
  /** Language the call was actually spoken in. */
  spokenLanguage: TranscriptLanguage;
  /** Language the user's phone is set to — the one-tap default. */
  phoneLanguage: TranscriptLanguage;
  /** null = showing the original transcript. */
  translateTo: TranscriptLanguage | null;
  onChange: (lang: TranscriptLanguage | null) => void;
}
const nameOf = (code: TranscriptLanguage) => transcriptLanguages.find(l => l.code === code)?.name ?? code;

/**
 * Sits above a transcript spoken in another language. One tap translates to
 * the phone's language; the chevron lets the user pick any other language or
 * go back to the original.
 */
export function TranscriptTranslateBar({
  isLight,
  spokenLanguage,
  phoneLanguage,
  translateTo,
  onChange
}: TranscriptTranslateBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const translated = translateTo !== null;
  const oneTapTarget = phoneLanguage === spokenLanguage ? 'en' : phoneLanguage;
  return (
    // Sticky so it stays in view while the transcript scrolls to the latest line.
    <div className={`sticky top-0 z-20 px-5 pb-2 pt-2 backdrop-blur-xl ${isLight ? 'bg-slate-50/85' : 'bg-[#0A0A0A]/85'}`} data-id="element-423" data-mp="37ap87zohyepx">
      <div className={`flex items-center gap-2 rounded-xl border py-1.5 pl-2.5 pr-1.5 ${translated ? isLight ? 'border-vexu/25 bg-vexu/[0.06]' : 'border-vexu/25 bg-vexu/[0.08]' : isLight ? 'border-slate-200 bg-white' : 'border-white/[0.07] bg-white/[0.03]'}`} data-id="element-424" data-mp="3gcyg0hsdrtzm">
        <Languages className={`h-3.5 w-3.5 flex-shrink-0 ${translated ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-425" data-mp="3q4uevv2ajo4w" />
        <p className={`min-w-0 flex-1 truncate text-[11px] ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-426" data-mp="3x4lgjev49c1">
          {translated ? <>
              Translated from {nameOf(spokenLanguage)} to{' '}
              <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-427" data-mp="kwxmrresf6js">
                {nameOf(translateTo)}
              </span>
            </> : <>
              Spoken in{' '}
              <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-428" data-mp="topx823tafod">
                {nameOf(spokenLanguage)}
              </span>
            </>}
        </p>

        {translated ? <button type="button" onClick={() => onChange(null)} className={`flex-shrink-0 rounded-lg px-2 py-1 text-[11px] font-semibold transition-colors ${isLight ? 'text-slate-600 hover:bg-white' : 'text-zinc-300 hover:bg-white/5'}`} data-id="element-429" data-mp="18mrz87v72t3i">
            Show original
          </button> : <button type="button" onClick={() => onChange(oneTapTarget)} className="flex-shrink-0 rounded-lg bg-vexu px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-vexu-600" data-id="element-430" data-mp="1rjs5xfiq5ahl">
            Translate to {nameOf(oneTapTarget)}
          </button>}
        <button type="button" onClick={() => setMenuOpen(o => !o)} aria-label="Choose transcript language" aria-expanded={menuOpen} className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-700' : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-200'}`} data-id="element-431" data-mp="1dp9ajo17778s">
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`} data-id="element-432" data-mp="q1hz0mnhey8k" />
        </button>
      </div>

      <AnimatePresence data-id="element-433" data-mp="l13pt1a59i8y">
        {menuOpen && <>
            <button type="button" aria-label="Close language menu" onClick={() => setMenuOpen(false)} className="fixed inset-0 z-30 cursor-default" data-id="element-434" data-mp="28y1td6jfk3b1" />
            <motion.ul role="listbox" initial={{
            opacity: 0,
            y: -4,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -4,
            scale: 0.97
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className={`absolute right-5 top-full z-40 mt-1 w-[210px] origin-top-right overflow-hidden rounded-xl py-1 shadow-lg ${isLight ? 'bg-white ring-1 ring-slate-200 shadow-slate-900/10' : 'bg-zinc-900 ring-1 ring-white/10 shadow-black/40'}`} data-id="element-435" data-mp="1zzzky5xda8dq">
              {transcriptLanguages.map(lang => {
              const isOriginal = lang.code === spokenLanguage;
              const selected = isOriginal ? translateTo === null : translateTo === lang.code;
              return <li key={lang.code} data-id="element-436" data-mp="14r5s7sospaev">
                    <button type="button" role="option" aria-selected={selected} onClick={() => {
                  onChange(isOriginal ? null : lang.code);
                  setMenuOpen(false);
                }} className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/5'}`} data-id="element-437" data-mp="zr1bq2nw0fon">
                      <span className="min-w-0 flex-1" data-id="element-438" data-mp="ureyvubz11gd">
                        <span className={`block text-[12px] font-medium ${isLight ? 'text-slate-800' : 'text-zinc-100'}`} data-id="element-439" data-mp="uszt1iudiidp">
                          {lang.name}
                        </span>
                        <span className={`block text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-440" data-mp="3tg3y32t5mfzf">
                          {isOriginal ? 'Original' : lang.code === phoneLanguage ? 'Phone language' : lang.nativeName}
                        </span>
                      </span>
                      {selected && <Check className="h-3.5 w-3.5 text-vexu" data-id="element-441" data-mp="1efeh0dvggx3q" />}
                    </button>
                  </li>;
            })}
            </motion.ul>
          </>}
      </AnimatePresence>
    </div>
  );
}